import type { RouteRecordRaw } from 'vue-router'
import {
  SCAN_PAGE,
  STAFF_PAGE,
  STAFF_PUMPING_PROTECTION,
  STAFF_DEPLOYMENT_PROTECTION,
  STAFF_CUSTOMERS_LIST,
  STAFF_CUSTOMERS_CREATE,
  STAFF_ASSIGNMENTS_LIST,
  STAFF_ASSIGNMENTS_CREATE,
  STAFF_ASSIGNMENTS_OPTIMIZATION_REPORT,
  STAFF_BASE_PRICE_PAGE,
  STAFF_CUSTOMERS_PRICES,
  STAFF_ASSIGNMENT_PRICES,
  STAFF_ASSIGNMENTS_SINGLE_EDIT,
  STAFF_STATISTICS,
  STAFF_FINANCIAL,
  STAFF_SENDER_ADDRESS_PRICES,
  STAFF_PICKUPS_LIST,
  STAFF_SHIPMENTS,
  STAFF_ASSIGNMENTS_DETAILS,
  PROXY_MESSAGES,
  CARRIER_MESSAGES,
  FORWARDING_HUB,
  STAFF_REPORTS,
  STAFF_AGREEMENT_COMPARE,
  STAFF_INVENTORY,
  STAFF_INVENTORY_CUSTOMER,
  STAFF_INVENTORY_INBOUND_RECEIPTS,
  STAFF_INVENTORY_PRODUCTS,
  STAFF_INVENTORY_TRANSACTIONS,
  STAFF_USERS_LIST,
  STAFF_ADDRESS_BOOK,
  STAFF_ADDRESS_BOOK_COMPANY,
  STAFF_AUDIT_LOGS,
} from '@/types/router'

const staff: Array<RouteRecordRaw> = [
  {
    path: '/agreement-compare',
    component: () => import('@/views/Staff/Index.vue'),
    meta: {
      auth: true,
      resource: 'staff.area',
      action: 'manage',
    },
    children: [
      {
        path: '',
        name: STAFF_AGREEMENT_COMPARE,
        component: () => import('@/views/Agreements/Compare.vue'),
        meta: {
          auth: true,
          resource: 'staff.area',
          action: 'manage',
          title: 'Agreement compare',
          containerClass: 'container',
        },
      },
    ],
  },
  {
    path: '/staff',
    name: STAFF_PAGE,
    redirect: '/staff/customers',
    component: () => import('@/views/Staff/Index.vue'),
    meta: {
      auth: true,
      resource: 'staff.area',
      action: 'manage',
      title: 'Staff',
    },
    children: [
      {
        path: 'pumping-protection',
        redirect: { name: STAFF_PUMPING_PROTECTION },
      },
      {
        path: 'messaging-protection',
        component: () => import('@/views/Staff/MessagingProtection/Base.vue'),
        redirect: { name: STAFF_DEPLOYMENT_PROTECTION },
        meta: { title: 'Messaging Protection', staffAdminOnly: true, containerClass: 'container-large' },
        children: [
          { path: 'evaluation', name: STAFF_PUMPING_PROTECTION, component: () => import('@/views/Staff/PumpingProtection/Index.vue') },
          { path: 'deployments', name: STAFF_DEPLOYMENT_PROTECTION, component: () => import('@/views/Staff/MessagingProtection/Deployment.vue') },
        ],
      },
      {
        path: 'shipments',
        name: STAFF_SHIPMENTS,
        component: () => import('@/views/Shipments/Index.vue'),
        meta: {
          title: 'Shipments feed',
          containerClass: 'container-large',
        },
      },
      {
        path: 'scan',
        name: SCAN_PAGE,
        component: () => import('@/views/Staff/Scan/Index.vue'),
        meta: {
          title: 'Scan',
          containerClass: 'container-large',
        },
      },
      {
        path: 'proxy',
        component: () => import('@/views/Staff/Proxy/Base.vue'),
        children: [
          {
            path: 'messages',
            component: () => import('@/views/Staff/Proxy/Messages/Index.vue'),
            name: PROXY_MESSAGES,
            meta: {
              title: 'Proxy messages',
              containerClass: 'container-large',
              staffAdminOnly: true,
            },
          },
          {
            path: 'carrier-messages',
            component: () => import('@/views/Staff/Proxy/Carrier/Index.vue'),
            name: CARRIER_MESSAGES,
            meta: {
              title: 'Carrier and recipient messages',
              containerClass: 'container-large',
              staffAdminOnly: true,
            },
          },
          {
            path: 'forwarding',
            component: () => import('@/views/Staff/Proxy/Forwarding/Index.vue'),
            name: FORWARDING_HUB,
            meta: {
              title: 'Service-point forwarding hub',
              containerClass: 'container-large',
              staffAdminOnly: true,
            },
          },
        ],
      },
      {
        path: 'pickups',
        name: STAFF_PICKUPS_LIST,
        component: () => import('@/views/Staff/Pickup/Index.vue'),
        meta: {
          title: 'Pickups',
          containerClass: 'container-large',
          staffAdminOnly: true,
        },
      },
      {
        path: 'reports',
        name: STAFF_REPORTS,
        component: () => import('@/views/Reports/Index.vue'),
        meta: {
          title: 'Reports',
          containerClass: 'container-large',
          staffAdminOnly: true,
        },
      },
      {
        path: 'staff-users',
        name: STAFF_USERS_LIST,
        component: () => import('@/views/Staff/Users/Index.vue'),
        meta: {
          title: 'Staff users',
          containerClass: 'container-large',
          staffAdminOnly: true,
        },
      },
      {
        path: 'inventory',
        component: () => import('@/views/Staff/Inventory/InventoryRoot.vue'),
        meta: {
          title: 'Inventory Management',
          containerClass: 'container',
        },
        children: [
          {
            path: '',
            name: STAFF_INVENTORY,
            component: () => import('@/views/Staff/Inventory/SelectPage.vue'),
          },
          {
            path: ':customerId',
            name: STAFF_INVENTORY_CUSTOMER,
            component: () => import('@/views/Staff/Inventory/CustomerLayout.vue'),
            children: [
              {
                path: '',
                name: 'staff.inventory.redirect',
                redirect: (to) => ({
                  name: STAFF_INVENTORY_PRODUCTS,
                  params: { customerId: to.params.customerId },
                }),
              },
              {
                path: 'products',
                name: STAFF_INVENTORY_PRODUCTS,
                component: () => import('@/views/Staff/Inventory/ProductsPage.vue'),
              },
              {
                path: 'inbound-receipts',
                name: STAFF_INVENTORY_INBOUND_RECEIPTS,
                component: () => import('@/views/Staff/Inventory/InboundReceiptsPage.vue'),
              },
              {
                path: 'transactions',
                name: STAFF_INVENTORY_TRANSACTIONS,
                component: () => import('@/views/Staff/Inventory/TransactionsPage.vue'),
              },
            ],
          },
        ],
      },
      {
        path: 'base-price',
        component: () => import('@/views/Staff/BasePrice/Index.vue'),
        name: STAFF_BASE_PRICE_PAGE,
        meta: {
          title: 'Base Price',
          staffAdminOnly: true,
        },
      },
      {
        path: 'customers',
        component: () => import('@/views/Staff/Customers/Index.vue'),
        children: [
          {
            path: '',
            component: () => import('@/views/Staff/Customers/List.vue'),
            name: STAFF_CUSTOMERS_LIST,
            meta: {
              title: 'Customers',
              containerClass: 'container-large',
              staffAdminOnly: true,
            },
          },
          {
            path: 'create',
            component: () => import('@/views/Staff/Customers/Create.vue'),
            name: STAFF_CUSTOMERS_CREATE,
            meta: {
              title: 'Create new customer',
              staffAdminOnly: true,
            },
          },
          {
            path: ':id/prices',
            component: () => import('@/views/Staff/Customers/Prices.vue'),
            name: STAFF_CUSTOMERS_PRICES,
            meta: {
              title: 'Customer Prices',
              hideFooter: true,
              staffAdminOnly: true,
            },
          },
        ],
      },
      {
        path: 'assignments',
        component: () => import('@/views/Assignments/Index.vue'),
        children: [
          {
            path: '',
            component: () => import('@/views/Assignments/AssignmentsListPage.vue'),
            name: STAFF_ASSIGNMENTS_LIST,
            meta: {
              title: 'Assignments',
              containerClass: 'container-large',
            },
          },
          {
            path: 'create',
            component: () => import('@/views/Assignments/Create.vue'),
            name: STAFF_ASSIGNMENTS_CREATE,
            meta: {
              title: 'Create new assignment',
            },
          },
          {
            path: ':id',
            component: () => import('@/views/Assignments/Assignment.vue'),
            children: [
              {
                path: 'details',
                component: () => import('@/views/Assignments/Details/AssignmentDetails.vue'),
                name: STAFF_ASSIGNMENTS_DETAILS,
                meta: {
                  title: 'Assignment',
                  containerClass: 'container-fluid',
                  hideFooter: true,
                },
              },
              {
                path: 'report',
                component: () => import('@/views/Assignments/Report/Index.vue'),
                name: STAFF_ASSIGNMENTS_OPTIMIZATION_REPORT,
                meta: {
                  title: 'Report',
                  containerClass: 'container-fluid',
                  hideFooter: true,
                },
              },
              {
                path: 'edit',
                component: () => import('@/views/Assignments/Edit.vue'),
                name: STAFF_ASSIGNMENTS_SINGLE_EDIT,
                meta: {
                  title: 'Edit Single Assignment',
                },
              },
              {
                path: 'prices',
                component: () => import('@/views/Assignments/AssignmentPrices.vue'),
                name: STAFF_ASSIGNMENT_PRICES,
                meta: {
                  title: 'Prices',
                  containerClass: 'container-fluid',
                  hideFooter: true,
                },
              },
            ],
          },
        ],
      },
      {
        path: 'financial',
        component: () => import('@/views/Staff/Financial/Index.vue'),
        name: STAFF_FINANCIAL,
        meta: {
          title: 'Financial',
          containerClass: 'container-large',
          hideFooter: true,
          staffAdminOnly: true,
          resource: 'financial',
          action: 'view',
        },
      },
      {
        path: 'statistics',
        component: () => import('@/views/Statistics/Index.vue'),
        name: STAFF_STATISTICS,
        meta: {
          title: 'Statistics',
          containerClass: 'container-fluid',
          hideFooter: true,
          staffAdminOnly: true,
        },
      },
      {
        path: 'customer/:id/sender-addresses/prices',
        component: () => import('@/views/Staff/Customers/SenderAddressPrices.vue'),
        name: STAFF_SENDER_ADDRESS_PRICES,
        meta: {
          title: 'Sender Address Prices',
          containerClass: 'container-fluid',
          hideFooter: true,
          showSenderAddressActionMenu: true,
          staffAdminOnly: true,
        },
      },
      {
        path: 'address-book',
        name: STAFF_ADDRESS_BOOK,
        component: () => import('@/views/Staff/AddressBook/SelectPage.vue'),
        meta: {
          title: 'Address Book',
          containerClass: 'container',
        },
      },
      {
        path: 'address-book/:companyId',
        name: STAFF_ADDRESS_BOOK_COMPANY,
        component: () => import('@/views/Staff/AddressBook/AddressBookPage.vue'),
        meta: {
          title: 'Address Book',
          containerClass: 'container-large',
        },
      },
      {
        path: 'audit-logs',
        name: STAFF_AUDIT_LOGS,
        component: () => import('@/views/Staff/AuditLog/Index.vue'),
        meta: {
          title: 'Activity Logs',
        },
      },
    ],
  },
]

export default staff
